import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PipeInventoryService } from '../services/pipe-inventory.service';
import { PipeInventory, InventoryCategory, InventoryCategoriesResponse } from '../model/pipe-inventory.model';

@Component({
  selector: 'app-pipe-inventory',
  templateUrl: './pipe-inventory.component.html',
  styleUrls: ['./pipe-inventory.component.css']
})
export class PipeInventoryComponent implements OnInit {
  displayedColumns: string[] = [
    'pipeId', 'pipeTuboType', 'qtyOnStock', 'areaName', 'expCategoryName', 
    'zone', 'unitCost', 'minimumStock', 'maximumStock', 'stockStatus', 
    'totalValue', 'lastUpdated', 'actions'
  ];
  
  dataSource = new MatTableDataSource<PipeInventory>();
  pipeInventory: PipeInventory[] = [];
  categories: InventoryCategory[] = [];
  isLoading = false;
  
  // Summary statistics
  totalItems = 0;
  totalValue = 0;
  lowStockCount = 0;
  outOfStockCount = 0;
  
  // Filters
  searchQuery = '';
  selectedArea: number | null = null;
  selectedCategory: number | null = null;
  selectedZone: number | null = null;
  selectedStatus: string = '';

  constructor(
    private pipeInventoryService: PipeInventoryService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadPipeInventory();
    this.loadCategories();
    this.loadInventorySummary();
  }

  loadPipeInventory(): void {
    this.isLoading = true;
    this.pipeInventoryService.getPipeInventory().subscribe({
      next: (data) => {
        this.pipeInventory = data;
        this.dataSource.data = data;
        this.calculateTotals();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading pipe inventory:', error);
        this.snackBar.open('Error loading pipe inventory', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  loadCategories(): void {
    this.pipeInventoryService.getInventoryCategories().subscribe({
      next: (response: InventoryCategoriesResponse) => {
        this.categories = response.categories;
      },
      error: (error) => {
        console.error('Error loading categories:', error);
      }
    });
  }

  loadInventorySummary(): void {
    // Summary is calculated locally in calculateTotals() method
    // This method is kept for consistency but no longer calls external API
    console.log('Inventory summary will be calculated from local data');
  }

  calculateTotals(): void {
    this.totalItems = this.pipeInventory.length;
    this.totalValue = this.pipeInventory.reduce((sum, item) => sum + (item.totalValue || 0), 0);
    this.lowStockCount = this.pipeInventory.filter(item => 
      item.minimumStock > 0 && item.qtyOnStock <= item.minimumStock
    ).length;
    this.outOfStockCount = this.pipeInventory.filter(item => item.qtyOnStock === 0).length;
  }

  applyFilter(): void {
    let filteredInventory = this.pipeInventory;

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filteredInventory = filteredInventory.filter(item => 
        item.pipeTuboType.toLowerCase().includes(query) ||
        item.areaName.toLowerCase().includes(query) ||
        item.expCategoryName.toLowerCase().includes(query)
      );
    }

    if (this.selectedArea) {
      filteredInventory = filteredInventory.filter(item => item.areaCode === this.selectedArea);
    }

    if (this.selectedCategory) {
      filteredInventory = filteredInventory.filter(item => item.expCategoryId === this.selectedCategory);
    }

    if (this.selectedZone) {
      filteredInventory = filteredInventory.filter(item => item.zone === this.selectedZone);
    }

    if (this.selectedStatus) {
      filteredInventory = filteredInventory.filter(item => item.status === this.selectedStatus);
    }

    this.dataSource.data = filteredInventory;
  }

  clearFilters(): void {
    this.searchQuery = '';
    this.selectedArea = null;
    this.selectedCategory = null;
    this.selectedZone = null;
    this.selectedStatus = '';
    this.dataSource.data = this.pipeInventory;
  }

  addPipeInventory(): void {
    // TODO: Implement add pipe inventory dialog
    this.snackBar.open('Add pipe inventory dialog - TODO', 'Close', { duration: 3000 });
  }

  editPipeInventory(pipe: PipeInventory): void {
    // TODO: Implement edit pipe inventory dialog
    this.snackBar.open(`Edit ${pipe.pipeTuboType} - TODO`, 'Close', { duration: 3000 });
  }

  deletePipeInventory(pipe: PipeInventory): void {
    if (confirm(`Are you sure you want to delete ${pipe.pipeTuboType}?`)) {
      this.pipeInventoryService.deletePipeInventory(pipe.pipeId).subscribe({
        next: () => {
          this.snackBar.open('Pipe inventory item deleted successfully', 'Close', { duration: 3000 });
          this.loadPipeInventory();
        },
        error: (error) => {
          console.error('Error deleting pipe inventory:', error);
          this.snackBar.open('Error deleting pipe inventory item', 'Close', { duration: 3000 });
        }
      });
    }
  }

  updateStock(pipe: PipeInventory): void {
    // TODO: Implement stock update dialog
    this.snackBar.open(`Update stock for ${pipe.pipeTuboType} - TODO`, 'Close', { duration: 3000 });
  }

  getStockStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'in stock': return 'green';
      case 'low stock': return 'orange';
      case 'out of stock': return 'red';
      default: return 'gray';
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'in stock': return 'badge-success';
      case 'low stock': return 'badge-warning';
      case 'out of stock': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  exportToExcel(): void {
    this.snackBar.open('Exporting to Excel...', 'Close', { duration: 3000 });
    // TODO: Implement Excel export
  }

  exportToPDF(): void {
    this.snackBar.open('Exporting to PDF...', 'Close', { duration: 3000 });
    // TODO: Implement PDF export
  }

  refreshData(): void {
    this.loadPipeInventory();
    this.loadCategories();
    this.loadInventorySummary();
  }

  // Helper methods
  getUniqueAreas(): { areaCode: number; areaName: string }[] {
    const uniqueAreas = new Map();
    this.pipeInventory.forEach(item => {
      if (!uniqueAreas.has(item.areaCode)) {
        uniqueAreas.set(item.areaCode, { areaCode: item.areaCode, areaName: item.areaName });
      }
    });
    return Array.from(uniqueAreas.values());
  }

  getUniqueZones(): number[] {
    const zones = new Set<number>();
    this.pipeInventory.forEach(item => {
      if (item.zone) {
        zones.add(item.zone);
      }
    });
    return Array.from(zones).sort((a, b) => a - b);
  }

  getQuantityClass(pipe: PipeInventory): string {
    if (pipe.qtyOnStock === 0) {
      return 'quantity-out';
    } else if (pipe.minimumStock > 0 && pipe.qtyOnStock <= pipe.minimumStock) {
      return 'quantity-low';
    }
    return 'quantity-normal';
  }
}
